<template>
  <div class="reset-wrap">
    <div class="reset-card">
      <div class="reset-logo">Beyond<span>Website.ai</span></div>

      <!-- No token -->
      <div v-if="!token" class="reset-state">
        <div class="reset-icon">⚠️</div>
        <h2>Invalid link</h2>
        <p>This password reset link is missing or invalid. Request a new one from the login page.</p>
        <router-link to="/" class="reset-btn">Go to login</router-link>
      </div>

      <!-- Success -->
      <div v-else-if="success" class="reset-state">
        <div class="reset-icon">✅</div>
        <h2>Password updated!</h2>
        <p>Your password has been set. You can now sign in.</p>
        <router-link to="/" class="reset-btn">Sign in</router-link>
      </div>

      <!-- Form -->
      <template v-else>
        <h2>Set your password</h2>
        <p class="reset-sub">Choose a password for your BeyondWebsite.ai account.</p>
        <div v-if="error" class="reset-error">{{ error }}</div>
        <form @submit.prevent="handleReset" class="reset-form">
          <div class="reset-group">
            <label>New password</label>
            <input v-model="password" type="password" required minlength="6" placeholder="At least 6 characters" :disabled="loading" />
          </div>
          <div class="reset-group">
            <label>Confirm password</label>
            <input v-model="confirm" type="password" required placeholder="Repeat your password" :disabled="loading" />
          </div>
          <button type="submit" :disabled="loading" class="reset-btn">
            {{ loading ? 'Saving…' : 'Set password' }}
          </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import api from '../utils/api'

export default {
  name: 'ResetPassword',
  setup() {
    const route = useRoute()
    const token = ref(route.query.token || '')
    const password = ref('')
    const confirm = ref('')
    const loading = ref(false)
    const error = ref('')
    const success = ref(false)

    const handleReset = async () => {
      error.value = ''
      if (password.value !== confirm.value) {
        error.value = 'Passwords do not match.'
        return
      }
      loading.value = true
      try {
        await api.post('/auth/reset-password', { token: token.value, password: password.value })
        success.value = true
      } catch (err) {
        error.value = err.response?.data?.error || 'Failed to reset password. The link may have expired.'
      } finally {
        loading.value = false
      }
    }

    return { token, password, confirm, loading, error, success, handleReset }
  }
}
</script>

<style scoped>
.reset-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 18% 18%, rgba(124,109,250,0.24), transparent 30%),
              linear-gradient(135deg, #0f0f18 0%, #141420 48%, #090911 100%);
  padding: 1.5rem;
}

.reset-card {
  background: rgba(22,22,38,.85);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 24px;
  padding: 2.5rem 2rem;
  width: 100%;
  max-width: 420px;
  backdrop-filter: blur(24px);
}

.reset-logo {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 24px;
}
.reset-logo span { color: #7c6dfa; }

.reset-card h2 {
  margin: 0 0 6px;
  font-size: 1.6rem;
  font-weight: 780;
  color: #fff;
  letter-spacing: -.04em;
}

.reset-sub {
  margin: 0 0 1.5rem;
  color: rgba(255,255,255,.5);
  font-size: .9rem;
}

.reset-form { display: flex; flex-direction: column; gap: 1.25rem; }

.reset-group { display: flex; flex-direction: column; gap: .4rem; }

.reset-group label {
  font-size: .75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: rgba(255,255,255,.5);
}

.reset-group input {
  padding: .85rem 1rem;
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 12px;
  background: rgba(255,255,255,.07);
  color: #e2e2e8;
  font-size: .95rem;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  font-family: inherit;
}
.reset-group input:focus {
  border-color: #7c6dfa;
  box-shadow: 0 0 0 3px rgba(124,109,250,.18);
}
.reset-group input:disabled { opacity: .55; }

.reset-btn {
  display: block;
  width: 100%;
  padding: .9rem;
  background: linear-gradient(135deg, #7c6dfa, #34d399);
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: .96rem;
  font-weight: 800;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: opacity .15s;
  font-family: inherit;
  margin-top: .5rem;
  box-shadow: 0 12px 28px rgba(124,109,250,.25);
}
.reset-btn:hover:not(:disabled) { opacity: .9; }
.reset-btn:disabled { opacity: .5; cursor: not-allowed; }

.reset-error {
  background: rgba(248,113,113,.12);
  border: 1px solid rgba(248,113,113,.28);
  color: #f87171;
  padding: .8rem 1rem;
  border-radius: 10px;
  font-size: .88rem;
  margin-bottom: .5rem;
}

.reset-state {
  text-align: center;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: rgba(255,255,255,.6);
  font-size: .94rem;
  line-height: 1.6;
}
.reset-icon { font-size: 36px; }
</style>
